import React, { useState } from "react";
import { Box, Divider, Typography, Modal } from "@mui/material";
import MoveableImage from "../components/MoveableImage/MoveableImage";
import colors from "../styles/colors";
import FlipBookComponent from "../components/FlipBookComponent/FlipBookComponent";
import { contentBoxStyle, imageContainerStyle } from "../styles/commonStyles";

const Photography: React.FC = () => {
  const [isJapan1Open, setIsJapan1Open] = useState(false);
  const [isJapan2Open, setIsJapan2Open] = useState(false);
  const [isUlefoneOpen, setIsUlefoneOpen] = useState(false);
  const [isTraktOpen, setIsTraktOpen] = useState(false);

  const handleOpenOverlay = (content: string) => {
    if (content === "japan1") {
      setIsJapan1Open(true);
    } else if (content === "japan2") {
      setIsJapan2Open(true);
    } else if (content === "ulefone") {
      setIsUlefoneOpen(true);
    } else if (content === "trakt") {
      setIsTraktOpen(true);
    }
  };

  const handleCloseOverlay = (content: string) => {
    if (content === "japan1") {
      setIsJapan1Open(false);
    } else if (content === "japan2") {
      setIsJapan2Open(false);
    } else if (content === "ulefone") {
      setIsUlefoneOpen(false);
    } else if (content === "trakt") {
      setIsTraktOpen(false);
    }
  };

  // Generate array of image paths for Japan 1 using PUBLIC_URL
  const japan1 = Array.from(
    { length: 16 },
    (_, i) => `${process.env.PUBLIC_URL}/japan1/${i + 1}.png`
  );

  // Generate array of image paths for Japan 2 using PUBLIC_URL
  const japan2 = Array.from(
    { length: 20 },
    (_, i) => `${process.env.PUBLIC_URL}/japan2/${i + 1}.png`
  );

  // Generate array of image paths for Ulefone using PUBLIC_URL
  const ulefone = [
    `${process.env.PUBLIC_URL}/ulefone/UlefoneMix.png`,
    ...Array.from(
      { length: 35 },
      (_, i) => `${process.env.PUBLIC_URL}/ulefone/UlefoneMix${i + 2}.png`
    ),
  ];

  // Generate array of image paths for Trakt using PUBLIC_URL
  const trakt = Array.from(
    { length: 16 },
    (_, i) => `${process.env.PUBLIC_URL}/trakt/${i + 1}.png`
  );

  return (
    <Box id="content" sx={contentBoxStyle}>
      <Box
        id="text-container"
        sx={{
          maxWidth: "1564px",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // 1 column on extra small screens
            sm: "1fr 1fr", // 2 columns on small screens (1:1 ratio)
            md: "2fr 2fr", // 3 columns on medium screens (1:2 ratio)
          },
          gap: "2rem",
          paddingTop: "10rem",
          paddingBottom: "5rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            wordBreak: "break-word",
            borderRadius: "0px 0px 0px 0px",
            boxShadow: "2px 2px 4px rgba(130, 143, 200, 0.5)",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: colors.filterColors[1],
              margin: "1rem",
            }}
          >
            Photography
          </Typography>
        </Box>
        <Box
          sx={{
            color: "white",
            borderRadius: "50px 0px 50px 0px",
            backgroundColor: colors.filterColors[1],
          }}
        >
          <Typography variant="body1" margin="1.5rem 1.8rem">
            <p>
            My passion for photography grew as I started learning the basic
            principles of design. However, unlike traditional design work,
            photography allows me to create a visual copy of a moment I have
            experienced — a chance to invite the viewer into the world as I see
            it.
            </p>
            <p>
            I am not trying to capture the perfect shot. Instead, my approach is
            about capturing fleeting moments, the kind of things we often pass
            by without noticing.
            </p>
            <p>
            The following zines showcase this philosophy, featuring a curated
            selection of photographs from my "Connected To" series. These
            digital versions highlight the beauty in everyday moments, offering
            a glimpse into the world through my eyes.
            </p>
          </Typography>
        </Box>
      </Box>
      <Box id="image-container" sx={imageContainerStyle}>
        <MoveableImage
          src={japan1[0]}
          buttonText="View Zine"
          alt="Digitalized Zine called japan 1/2 from the series Connected To"
          onButtonClick={() => handleOpenOverlay("japan1")}
        />
        <MoveableImage
          src={japan2[0]}
          buttonText="View Zine"
          alt="Digitalized Zine called japan 2/2 from the series Connected To"
          onButtonClick={() => handleOpenOverlay("japan2")}
        />
        <MoveableImage
          src={ulefone[0]}
          buttonText="View Zine"
          alt="Digitalized Zine called ulefone mix from the series Connected To"
          onButtonClick={() => handleOpenOverlay("ulefone")}
        />
        <MoveableImage
          src={trakt[0]}
          buttonText="View Zine"
          alt="Digitalized Zine called trakt from the series Connected To"
          onButtonClick={() => handleOpenOverlay("trakt")}
        />
      </Box>

      {/* Modal for Japan 1 */}
      <Modal
        open={isJapan1Open}
        onClose={() => handleCloseOverlay("japan1")}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "50%",
            height: "50%",
            backgroundColor: "rgb(216, 216, 216)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FlipBookComponent images={japan1} />
        </Box>
      </Modal>
      {/* Modal for Japan 2 */}
      <Modal
        open={isJapan2Open}
        onClose={() => handleCloseOverlay("japan2")}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "50%",
            height: "50%",
            backgroundColor: "rgb(216, 216, 216)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FlipBookComponent images={japan2} />
        </Box>
      </Modal>
      {/* Modal for Ulefone */}
      <Modal
        open={isUlefoneOpen}
        onClose={() => handleCloseOverlay("ulefone")}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "50%",
            height: "50%",
            backgroundColor: "rgb(216, 216, 216)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FlipBookComponent images={ulefone} />
        </Box>
      </Modal>
      {/* Modal for Trakt */}
      <Modal
        open={isTraktOpen}
        onClose={() => handleCloseOverlay("trakt")}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "50%",
            height: "50%",
            backgroundColor: "rgb(216, 216, 216)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FlipBookComponent images={trakt} />
        </Box>
      </Modal>
    </Box>
  );
};

export default Photography;
